
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import type { NewsTag } from '../../app/services/api/news/newsManagementTypes';
import { useNewsManagementClient } from '../../hooks/api/useNewsManagementClient';

interface NewsTaxonomyManagerProps {
  open: boolean;
  tenantId: number;
  onClose: () => void;
  onChanged?: () => void;
}

const emptyDraft = {
  name: '',
  description: '',
  color: '#1976d2',
};

export function NewsTaxonomyManager({
  open,
  tenantId,
  onClose,
  onChanged,
}: NewsTaxonomyManagerProps) {
  const client = useNewsManagementClient();
  const [tags, setTags] = useState<NewsTag[]>([]);
  const [selected, setSelected] = useState<NewsTag | null>(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [retireTarget, setRetireTarget] = useState<NewsTag | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const result = await client.getTags(tenantId, false);
    setTags(result);
  }, [client, tenantId]);

  useEffect(() => {
    if (!open) return;
    load().catch(() => setError('Unable to load News taxonomy.'));
  }, [load, open]);

  const beginCreate = () => {
    setSelected(null);
    setDraft(emptyDraft);
    setError(null);
  };

  const beginEdit = (tag: NewsTag) => {
    setSelected(tag);
    setDraft({
      name: tag.name,
      description: tag.description ?? '',
      color: tag.color || '#1976d2',
    });
    setError(null);
  };

  const save = async () => {
    if (!draft.name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      if (selected) {
        await client.updateTag(
          selected,
          draft.name.trim(),
          draft.description.trim() || undefined,
          draft.color || undefined,
        );
      } else {
        await client.createTag(
          tenantId,
          draft.name.trim(),
          draft.description.trim() || undefined,
          draft.color || undefined,
        );
      }
      await load();
      beginCreate();
      onChanged?.();
    } catch {
      setError('Unable to save this News tag. It may have changed since you opened it.');
    } finally {
      setBusy(false);
    }
  };

  const retire = async () => {
    if (!retireTarget) return;
    setBusy(true);
    setError(null);
    try {
      await client.retireTag(retireTarget);
      await load();
      if (selected?.id === retireTarget.id) beginCreate();
      setRetireTarget(null);
      onChanged?.();
    } catch {
      setError('Unable to retire this News tag. Refresh and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth>
        <DialogTitle>News Taxonomy</DialogTitle>
        <DialogContent>
          <Stack spacing={3}
            sx={{ pt: 1 }}>
            {error && <Chip label={error}
              color="error"
              variant="outlined" />}

            <Stack direction={{ xs: 'column', md: 'row' }}
              spacing={3}
              alignItems="flex-start">
              <Box sx={{ flex: 1, width: '100%' }}>
                <Stack direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}>
                  <Typography variant="textMD"
                    fontWeight={700}>Tags</Typography>
                  <Button variant="secondary"
                    size="md"
                    onClick={beginCreate}>New Tag</Button>
                </Stack>

                <Stack spacing={1}>
                  {tags.map(tag => (
                    <Box
                      key={tag.id}
                      sx={{
                        p: 1.5,
                        border: 1,
                        borderColor: selected?.id === tag.id ? 'primary.main' : 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Stack direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={1}>
                        <Box>
                          <Stack direction="row"
                            spacing={1}
                            alignItems="center">
                            <Box
                              sx={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: tag.color || 'text.disabled',
                              }}
                            />
                            <Typography variant="textSM"
                              fontWeight={700}>{tag.name}</Typography>
                            <Chip
                              size="small"
                              label={tag.isActive ? 'Active' : 'Retired'}
                              color={tag.isActive ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </Stack>
                          {tag.description && (
                            <Typography variant="textXS"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}>
                              {tag.description}
                            </Typography>
                          )}
                          <Typography variant="textXS"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}>
                            Used by {tag.usageCount} News revision{tag.usageCount === 1 ? '' : 's'}
                          </Typography>
                        </Box>
                        <Button variant="secondary"
                          size="md"
                          onClick={() => beginEdit(tag)}>
                          View / Edit
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>

              <Divider orientation="vertical"
                flexItem />

              <Stack spacing={2}
                sx={{ flex: 1, width: '100%' }}>
                <Typography variant="textMD"
                  fontWeight={700}>
                  {selected ? 'Edit Tag' : 'Create Tag'}
                </Typography>

                {selected && (
                  <Stack direction="row"
                    spacing={1}>
                    <Chip
                      size="small"
                      label={selected.isActive ? 'Active' : 'Retired'}
                      color={selected.isActive ? 'success' : 'default'}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={String(selected.usageCount) + ' revision' + (selected.usageCount === 1 ? '' : 's')}
                      variant="outlined"
                    />
                  </Stack>
                )}

                <TextField
                  label="Name"
                  value={draft.name}
                  onChange={event => setDraft(current => ({ ...current, name: event.target.value }))}
                  inputProps={{ maxLength: 100 }}
                  disabled={selected?.isActive === false}
                  fullWidth
                />

                <TextField
                  label="Description"
                  value={draft.description}
                  onChange={event => setDraft(current => ({ ...current, description: event.target.value }))}
                  inputProps={{ maxLength: 500 }}
                  disabled={selected?.isActive === false}
                  multiline
                  minRows={3}
                  fullWidth
                />

                <Stack direction="row"
                  spacing={2}
                  alignItems="center">
                  <TextField
                    label="Colour"
                    type="color"
                    value={draft.color}
                    onChange={event => setDraft(current => ({ ...current, color: event.target.value }))}
                    disabled={selected?.isActive === false}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140 }}
                  />
                  <Typography variant="textXS"
                    color="text.secondary">
                    Used to visually distinguish categories in management and consumer views.
                  </Typography>
                </Stack>

                {selected?.isActive !== false && (
                  <Stack direction="row"
                    spacing={1}>
                    <Button
                      variant="primary"
                      size="md"
                      disabled={busy || !draft.name.trim()}
                      onClick={() => save().catch(() => undefined)}
                    >
                      {selected ? 'Save Changes' : 'Create Tag'}
                    </Button>
                    {selected && (
                      <Button
                        variant="secondary"
                        size="md"
                        disabled={busy}
                        onClick={() => setRetireTarget(selected)}
                      >
                        Retire Tag
                      </Button>
                    )}
                  </Stack>
                )}

                {selected && !selected.isActive && (
                  <Typography variant="textSM"
                    color="text.secondary">
                    This tag is retired. It remains visible for historical News but cannot be newly assigned.
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="secondary"
            size="md"
            onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={retireTarget !== null}
        onClose={() => setRetireTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Retire News Tag</DialogTitle>
        <DialogContent>
          <Typography variant="textSM">
            {retireTarget?.usageCount
              ? '"' + retireTarget.name + '" is used by ' + retireTarget.usageCount + ' News revision' + (retireTarget.usageCount === 1 ? '' : 's') + '. Retiring it will stop new assignment but historical News will keep the tag.'
              : 'Retire "' + (retireTarget?.name ?? '') + '"? It will no longer be available for new News items.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            size="md"
            onClick={() => setRetireTarget(null)}>Cancel</Button>
          <Button
            variant="primary"
            size="md"
            disabled={busy}
            onClick={() => retire().catch(() => undefined)}
          >
            Retire Tag
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
